// src/pages/InternationalContactsList.js
import React, { useState, useEffect, useMemo } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Spinner,
  FormGroup,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  UncontrolledTooltip,
  Badge,
} from "reactstrap"
import TableContainer from "../Common/TableContainer"
import axios from "axios"
import apiURL from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ContactEditModal from "./ContactEditModal" // adjust path
import ExcelDownload from "components/Common/ExcelDownload"

import pakistanCitiesData from "../../pages/Cities/pakistan.json"
import citiesDataGlobal from "../../pages/Cities/cities.json"

const InternationalContactsList = ({ type, pageTitle }) => {
  // Set page title
  document.title = pageTitle

  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(false)

  // Filters
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState({
    city: "",
    country: "",
    phone: "",
    cnic: "",
    startDate: null,
    endDate: null,
  })

  // Modals & selection
  const [citiesData, setCitiesData] = useState(citiesDataGlobal)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)

  const [dropdownOpen, setDropdownOpen] = useState({})

  // =========================
  // API: Fetch contacts
  // =========================
  const fetchContacts = async () => {
    try {
      setLoading(true)
      const { data } = await axios.get(`${apiURL}/international/contacts`, {
        params: { type },
      })
      if (data.status === "success") {
        setContacts(data.contacts)
      } else {
        toastr.error(data.message || "Failed to fetch contacts")
      }
    } catch (err) {
      console.error("Failed to fetch contacts:", err)
      toastr.error("Failed to fetch contacts")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContacts()
  }, [type])

  // =========================
  // Filtering
  // =========================
  const filteredContacts = useMemo(() => {
    return contacts.filter(c => {
      const createdAt = c.created_at ? new Date(c.created_at) : null

      return (
        (!filters.city ||
          (c.city_name || "")
            .toLowerCase()
            .includes(filters.city.toLowerCase())) &&
        (!filters.country ||
          (c.country_name || "")
            .toLowerCase()
            .includes(filters.country.toLowerCase())) &&
        (!filters.phone ||
          (c.phone || "").includes(filters.phone) ||
          (c.phone2 || "").includes(filters.phone)) &&
        (!filters.cnic ||
          (c.cnic || "").toLowerCase().includes(filters.cnic.toLowerCase())) &&
        (!filters.startDate ||
          (createdAt && createdAt >= new Date(filters.startDate))) &&
        (!filters.endDate ||
          (createdAt && createdAt <= new Date(filters.endDate)))
      )
    })
  }, [contacts, filters])

  // =========================
  // Helpers
  // =========================
  const toggleDropdown = id => {
    setDropdownOpen(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const formatDate = dateStr => {
    if (!dateStr) return "----"
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const handleDelete = async id => {
    try {
      await axios.delete(`${apiURL}/international/contacts/${id}`)
      toastr.success("Contact deleted successfully")
      setDeleteModalOpen(false)
      fetchContacts()
    } catch (err) {
      console.error("Delete failed:", err)
      toastr.error("Failed to delete contact")
    }
  }

  const openViewModal = contact => {
    setSelectedContact(contact)
    setViewModalOpen(true)
  }

  const openEditModal = contact => {
    setSelectedContact(contact)
    setEditModalOpen(true)
  }

  // =========================
  // Columns for TableContainer
  // =========================
  const columns = useMemo(
    () => [
      {
        Header: "Sr #",
        accessor: "sr",
        disableFilters: true,
        Cell: ({ row }) => <b>{row.index + 1}</b>,
      },

      {
        Header: "Name",
        accessor: "name",
        disableFilters: true,
        Cell: ({ row }) => {
          const c = row.original
          return (
            <div className="d-flex align-items-center gap-2">
              <i className="bx bx-user text-primary" />
              <span>
                {`${c.first_name || ""} ${c.last_name || ""}`.trim() || "--"}
              </span>
            </div>
          )
        },
      },
      {
        Header: "Contact",
        accessor: "phone",
        disableFilters: true,
        Cell: ({ row }) => {
          const c = row.original
          return (
            <div className="d-flex align-items-center gap-2">
              <i className="bx bx-phone text-success" />
              <span>{c.phone || "--"}</span>
            </div>
          )
        },
      },
      {
        Header: "Email",
        accessor: "email",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-envelope text-info" />
            <span>{value || "--"}</span>
          </div>
        ),
      },
      ...(type === "sender"
        ? [
            {
              Header: "CNIC",
              accessor: "cnic",
              disableFilters: true,
              Cell: ({ value }) => (
                <div className="d-flex align-items-center gap-2">
                  <i className="bx bx-id-card text-warning" />
                  <span>{value || "--"}</span>
                </div>
              ),
            },
          ]
        : [
            {
              Header: "Country",
              accessor: "country_name",
              disableFilters: true,
              Cell: ({ value }) => (
                <div className="d-flex align-items-center gap-2">
                  <i className="bx bx-flag text-secondary" />
                  <span>{value || "--"}</span>
                </div>
              ),
            },
          ]),
      {
        Header: "City",
        accessor: "city_name",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-map text-danger" />
            <span>{value || "--"}</span>
          </div>
        ),
      },

      {
        Header: "Created",
        accessor: "created_at",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-calendar text-dark"></i>
            {formatDate(value)}
          </div>
        ),
      },
      {
        Header: "Actions",
        disableFilters: true,
        Cell: ({ row }) => {
          const contact = row.original
          const isOpen = dropdownOpen[contact.id]
          return (
            <div className="position-relative">
              <i
                className="bx bx-dots-vertical-rounded"
                style={{ cursor: "pointer", fontSize: "18px" }}
                onClick={() => toggleDropdown(contact.id)}
              />
              {isOpen && (
                <div
                  className="position-absolute bg-white shadow border rounded p-2"
                  style={{
                    top: "30px",
                    right: "0",
                    zIndex: 1050,
                    minWidth: "140px",
                  }}
                >
                  <Button
                    color="link"
                    className="text-primary text-start w-100 p-1 fs-6"
                    onClick={() => {
                      openViewModal(contact)
                      toggleDropdown(contact.id)
                    }}
                  >
                    <i className="bx bx-show me-1"></i>View
                  </Button>
                  <Button
                    color="link"
                    className="text-info text-start w-100 p-1 fs-6"
                    onClick={() => {
                      openEditModal(contact)
                      toggleDropdown(contact.id)
                    }}
                  >
                    <i className="bx bx-edit me-1"></i>Edit
                  </Button>
                  <Button
                    color="link"
                    className="text-danger text-start w-100 p-1 fs-6"
                    onClick={() => {
                      setSelectedContact(contact)
                      setDeleteModalOpen(true)
                      toggleDropdown(contact.id)
                    }}
                  >
                    <i className="bx bx-trash me-1"></i>Delete
                  </Button>
                </div>
              )}
            </div>
          )
        },
      },
    ],
    [contacts, dropdownOpen, type]
  )

  // Unique country list for receiver
  const receiverCountryOptions = useMemo(() => {
    const uniqueCountries = []
    const seen = new Set()
    for (let city of citiesData) {
      if (!seen.has(city.country_name)) {
        seen.add(city.country_name)
        uniqueCountries.push({
          value: city.country_name, // ✅ full country name
          label: city.country_name,
        })
      }
    }
    return uniqueCountries.sort((a, b) => a.label.localeCompare(b.label))
  }, [citiesData])

  const loadCities = async () => {
    try {
      const res = await axios.get(`${apiURL}/international/cities-options/list`)
      const db = res.data?.cities || []

      // DB cities
      const dbGlobal = db.map(c => ({
        name: c.city_name,
        country_name: c.country_name,
      }))

      // JSON cities
      const jsonGlobal = citiesDataGlobal.map(c => ({
        name: c.name,
        country_name: c.country_name,
      }))

      const mergedGlobal = [...jsonGlobal, ...dbGlobal]

      // Remove duplicates (country + city)
      const seen = new Set()
      const uniqueGlobal = mergedGlobal.filter(c => {
        const key = `${c.country_name.toLowerCase()}|${c.name.toLowerCase()}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })

      setCitiesData(uniqueGlobal)
    } catch (err) {
      console.error("City merge error:", err)
    }
  }

  useEffect(() => {
    loadCities()
  }, [])

  // =========================
  // Render
  // =========================
  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col xs="12">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 fw-semibold text-dark">
                {type === "sender" ? "All Senders" : "All Receivers"}
              </h4>
            </div>
          </Col>
        </Row>

        {/* Filters Accordion */}
        <Accordion
          open={filterOpen ? "1" : ""}
          toggle={() => setFilterOpen(!filterOpen)}
          className="mb-4"
        >
          <AccordionItem>
            <AccordionHeader
              targetId="1"
              className="fw-semibold bg-light rounded"
            >
              <i className="bx bx-filter-alt me-2"></i>Filters
            </AccordionHeader>
            <AccordionBody accordionId="1">
              <div className="p-3 bg-light rounded">
                <Row className="g-3">
                  <Col md={2}>
                    <Label className="form-label fw-medium">City</Label>
                    <Input
                      type="text"
                      placeholder="Search city"
                      value={filters.city}
                      onChange={e =>
                        setFilters({ ...filters, city: e.target.value })
                      }
                      className="form-control-sm"
                    />
                  </Col>

                  {type === "receiver" && (
                    <>
                      <Col md={2}>
                        <Label className="form-label fw-medium">Country</Label>
                        <Input
                          type="text"
                          placeholder="Country code/name"
                          value={filters.country}
                          onChange={e =>
                            setFilters({
                              ...filters,
                              country: e.target.value,
                            })
                          }
                          className="form-control-sm"
                        />
                      </Col>
                    </>
                  )}

                  {type === "sender" && (
                    <Col md={2}>
                      <Label className="form-label fw-medium">CNIC</Label>
                      <Input
                        type="text"
                        placeholder="CNIC"
                        value={filters.cnic}
                        onChange={e =>
                          setFilters({ ...filters, cnic: e.target.value })
                        }
                        className="form-control-sm"
                      />
                    </Col>
                  )}

                  <Col md={2}>
                    <Label className="form-label fw-medium">Phone</Label>
                    <Input
                      type="text"
                      placeholder="Phone"
                      value={filters.phone}
                      onChange={e =>
                        setFilters({ ...filters, phone: e.target.value })
                      }
                      className="form-control-sm"
                    />
                  </Col>

                  <Col md={2}>
                    <Label className="form-label fw-medium">From</Label>
                    <div className="position-relative">
                      <DatePicker
                        selected={filters.startDate}
                        onChange={date =>
                          setFilters({ ...filters, startDate: date })
                        }
                        placeholderText="Start date"
                        dateFormat="yyyy-MM-dd"
                        className="form-control form-control-sm ps-4"
                        isClearable
                      />
                      <i className="bx bx-calendar position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>
                    </div>
                  </Col>

                  <Col md={2}>
                    <Label className="form-label fw-medium">To</Label>
                    <div className="position-relative">
                      <DatePicker
                        selected={filters.endDate}
                        onChange={date =>
                          setFilters({ ...filters, endDate: date })
                        }
                        placeholderText="End date"
                        dateFormat="yyyy-MM-dd"
                        className="form-control form-control-sm ps-4"
                        isClearable
                      />
                      <i className="bx bx-calendar position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>
                    </div>
                  </Col>

                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setFilters({
                          city: "",
                          country: "",
                          phone: "",
                          cnic: "",
                          zip: "",
                          startDate: null,
                          endDate: null,
                        })
                      }
                      className="w-100"
                    >
                      <i className="bx bx-refresh me-1"></i>Clear
                    </Button>
                  </Col>
                </Row>
              </div>
            </AccordionBody>
          </AccordionItem>
        </Accordion>

        {/* Table Card */}
        <Card>
          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" className="mb-3" />
                <p className="text-muted">
                  Loading {type === "sender" ? "senders" : "receivers"}...
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <div className="float-end d-flex gap-2 mb-2">
                  {contacts.length > 0 && (
                    <ExcelDownload
                      leads={contacts}
                      title={
                        type === "sender"
                          ? "Download Senders"
                          : "Download Receivers"
                      }
                    />
                  )}
                </div>
                <TableContainer
                  columns={columns}
                  data={filteredContacts}
                  isGlobalFilter={true}
                  customPageSize={10}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* View Contact Modal */}
        <Modal
          isOpen={viewModalOpen}
          toggle={() => setViewModalOpen(false)}
          centered
          size="lg"
          className="contact-view-modal"
        >
          <ModalHeader
            toggle={() => setViewModalOpen(false)}
            className="border-0 pb-2"
          >
            <div className="d-flex align-items-center">
              <div className="bg-light-primary rounded-circle p-2 me-3">
                <i
                  className={`bx ${
                    type === "sender" ? "bx-user-pin" : "bx-user-check"
                  } fs-4 text-primary`}
                ></i>
              </div>
              <div>
                <h5 className="fw-bold mb-0">
                  {type === "sender" ? "Sender Details" : "Receiver Details"}
                </h5>
                <p className="text-muted small mb-0">Contact Information</p>
              </div>
            </div>
          </ModalHeader>

          <ModalBody className="pt-0">
            {selectedContact && (
              <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body p-4">
                  {/* Contact Header */}
                  <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                    <div className="bg-light-primary rounded-circle p-3 me-3">
                      <i className="bx bx-user-circle fs-2 text-primary"></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">
                        {`${selectedContact.first_name || ""} ${
                          selectedContact.last_name || ""
                        }`.trim() || "Unnamed Contact"}
                      </h6>
                      <span className="badge bg-light-primary text-primary small">
                        {type === "sender" ? "Sender" : "Receiver"}
                      </span>
                    </div>
                  </div>

                  <div className="row">
                    {/* Contact Details */}
                    <div className="col-6">
                      {/* Contact Info Section */}
                      <div className="mb-4">
                        <h6 className="fw-semibold text-muted mb-3 d-flex align-items-center">
                          <i className="bx bx-info-circle me-2"></i>
                          Contact Information
                        </h6>

                        <div className="row g-3">
                          {/* Phone */}
                          <div className="col-12">
                            <div className="d-flex align-items-start">
                              <div className="bg-light rounded-circle p-2 me-3">
                                <i className="bx bx-phone text-muted"></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">Phone</div>
                                <div className="fw-semibold">
                                  {selectedContact.phone || (
                                    <span className="text-muted fw-normal">
                                      --
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Alternate Phone */}
                          <div className="col-12">
                            <div className="d-flex align-items-start">
                              <div className="bg-light rounded-circle p-2 me-3">
                                <i className="bx bx-mobile-alt text-muted"></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">
                                  Alternate Phone
                                </div>
                                <div className="fw-semibold">
                                  {selectedContact.phone2 || (
                                    <span className="text-muted fw-normal">
                                      --
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Email */}
                          <div className="col-12">
                            <div className="d-flex align-items-start">
                              <div className="bg-light rounded-circle p-2 me-3">
                                <i className="bx bx-envelope text-muted"></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">Email</div>
                                <div className="fw-semibold text-break">
                                  {selectedContact.email || (
                                    <span className="text-muted fw-normal">
                                      --
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      {/* Specific Details Section */}
                      {(selectedContact.cnic ||
                        selectedContact.zip ||
                        selectedContact.country_name) && (
                        <div className="mb-4">
                          <h6 className="fw-semibold text-muted mb-3 d-flex align-items-center">
                            <i className="bx bx-id-card me-2"></i>
                            {type === "sender"
                              ? "Sender Details"
                              : "Receiver Details"}
                          </h6>

                          <div className="row g-3">
                            {/* SENDER: CNIC */}
                            {type === "sender" && selectedContact.cnic && (
                              <div className="col-12">
                                <div className="d-flex align-items-start">
                                  <div className="bg-light rounded-circle p-2 me-3">
                                    <i className="bx bx-card text-muted"></i>
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="text-muted small">CNIC</div>
                                    <div className="fw-semibold">
                                      {selectedContact.cnic}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* RECEIVER: ZIP & Country */}
                            {type === "receiver" && (
                              <>
                                {selectedContact.zip && (
                                  <div className="col-12">
                                    <div className="d-flex align-items-start">
                                      <div className="bg-light rounded-circle p-2 me-3">
                                        <i className="bx bx-map-pin text-muted"></i>
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="text-muted small">
                                          ZIP Code
                                        </div>
                                        <div className="fw-semibold">
                                          {selectedContact.zip}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {selectedContact.country_name && (
                                  <div className="col-12">
                                    <div className="d-flex align-items-start">
                                      <div className="bg-light rounded-circle p-2 me-3">
                                        <i className="bx bx-globe text-muted"></i>
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="text-muted small">
                                          Country
                                        </div>
                                        <div className="fw-semibold">
                                          {selectedContact.country_name}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-6">
                      {/* Address Section */}
                      <div className="mb-4">
                        <h6 className="fw-semibold text-muted mb-3 d-flex align-items-center">
                          <i className="bx bx-home me-2"></i>
                          Address Details
                        </h6>

                        <div className="row g-3">
                          {/* City */}
                          {selectedContact.city_name && (
                            <div className="col-12">
                              <div className="d-flex align-items-start">
                                <div className="bg-light rounded-circle p-2 me-3">
                                  <i className="bx bx-building text-muted"></i>
                                </div>
                                <div className="flex-grow-1">
                                  <div className="text-muted small">City</div>
                                  <div className="fw-semibold">
                                    {selectedContact.city_name}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Address */}
                          <div className="col-12">
                            <div className="d-flex align-items-start">
                              <div className="bg-light rounded-circle p-2 me-3">
                                <i className="bx bx-map text-muted"></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">Address</div>
                                <div className="fw-semibold">
                                  {selectedContact.full_address ||
                                    selectedContact.current_address || (
                                      <span className="text-muted fw-normal">
                                        --
                                      </span>
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      {/* Metadata */}
                      <div className="mt-4 pt-3 border-top">
                        <div className="d-flex align-items-start">
                          <div className="bg-light rounded-circle p-2 me-3">
                            <i className="bx bx-calendar text-muted"></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="text-muted small">Created At</div>
                            <div className="fw-semibold">
                              {formatDate(selectedContact.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>

          <ModalFooter className="border-0 pt-0">
            <Button
              color="light"
              size="sm"
              onClick={() => setViewModalOpen(false)}
              className="border px-4"
            >
              <i className="bx bx-x me-1"></i> Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit Contact Modal (uses your existing ContactEditModal) */}
        {selectedContact && (
          <ContactEditModal
            isOpen={editModalOpen}
            toggle={() => setEditModalOpen(false)}
            contact={selectedContact}
            type={type}
            citiesData={citiesData}
            loadCities={loadCities}
            receiverCountryOptions={receiverCountryOptions}
            onSave={updated => {
              // Update list in-place
              setContacts(prev =>
                prev.map(c => (c.id === updated.id ? { ...c, ...updated } : c))
              )
              toastr.success("Contact updated successfully")
              setEditModalOpen(false)
            }}
          />
        )}

        {/* Delete Modal */}
        <Modal
          isOpen={deleteModalOpen}
          toggle={() => setDeleteModalOpen(false)}
          centered
        >
          <ModalHeader toggle={() => setDeleteModalOpen(false)}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            Are you sure you want to delete contact{" "}
            <strong>#{selectedContact?.id}</strong>?
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDelete(selectedContact.id)}
            >
              Delete
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  )
}

export default InternationalContactsList
